import React from 'react';
import { createPortal } from 'react-dom';
import { FmlxBanner } from 'fmlx-common-ui';

export type OverlayBannerVariant = 'info' | 'warning' | 'error';

export type OverlayBannerPosition =
  | 'top'
  | 'bottom'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export interface OverlayBannerProps {
  /** Controls visibility. When false the overlay is not rendered at all. */
  show: boolean;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  variant?: OverlayBannerVariant;
  position?: OverlayBannerPosition;
  hideCloseButton?: boolean;
  onCloseClick?: () => void;
  id?: string;
  /** Extra classes for the fixed wrapper (e.g. to widen it or tweak offset). */
  className?: string;
}

// Fixed-position placement classes kept as literals so Tailwind can detect them.
// Centered positions use `inset-x-0 mx-auto` (auto side-margins center the
// fit-content box within the viewport) rather than translate, so alignment does
// not depend on the wrapper's computed width.
const POSITION_CLASSES: Record<OverlayBannerPosition, string> = {
  top: 'top-2 inset-x-0 mx-auto w-fit',
  bottom: 'bottom-2 inset-x-0 mx-auto w-fit',
  'top-left': 'top-2 left-2 w-auto',
  'top-right': 'top-2 right-2 w-auto',
  'bottom-left': 'bottom-2 left-2 w-auto',
  'bottom-right': 'bottom-2 right-2 w-auto',
};

/**
 * Persistent, non-blocking banner that floats on top of the page in a fixed
 * position (like the app snackbar, but it stays until `show` is toggled off).
 * Reusable across features — wrap it in a feature component that supplies the
 * `show` condition, message, and variant.
 */
const OverlayBanner = (props: OverlayBannerProps) => {
  const {
    show,
    title,
    subtitle = '',
    variant = 'warning',
    position = 'top',
    hideCloseButton = false,
    onCloseClick,
    id,
    className = '',
  } = props;

  if (!show || typeof document === 'undefined') {
    return null;
  }

  // Portal to <body> so the fixed overlay is positioned relative to the
  // viewport regardless of the calling component's ancestors (a transformed /
  // filtered / contained ancestor would otherwise become the containing block
  // and break viewport centering).
  return createPortal(
    <div
      className={`fixed z-[3000] max-w-[50rem] ${POSITION_CLASSES[position]} ${className}`.trim()}
    >
      <FmlxBanner
        id={id}
        variant={variant}
        title={title}
        subtitle={subtitle}
        hideCloseButton={hideCloseButton}
        onCloseClick={onCloseClick}
        show
      />
    </div>,
    document.body,
  );
};

export default OverlayBanner;
